<header class="bg-primary sticky top-0 z-50 shadow-md">
  <div class="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex items-center justify-between h-16 md:h-20">
      <!-- Logo -->
      <div class="flex-shrink-0">
        <button (click)="navigateToSection({external: false ,label: '', route: '/accueil', fragment: 'installation' })" class="block">
          <img
            ngSrc="/logo_noya.png"
            alt="NOYA Energie Photovoltaïque"
            class="h-8 sm:h-10 md:h-12 w-auto cursor-pointer hover:opacity-90 transition-opacity"
            height="91"
            width="177"
            priority
          />
        </button>
      </div>

      <!-- Desktop Navigation -->
      @if ((isLessThanMD$ | async) === false) {
        <nav class="hidden md:flex items-center space-x-6 lg:space-x-20 flex-1 justify-center">
          @for (item of navItems(); track item.label) {
            <button
              (click)="navigateToSection(item)"
              [class.font-bold]="activeSection() === item.fragment"
              [class.font-medium]="activeSection() !== item.fragment"
              class="text-on-primary text-sm lg:text-base hover:opacity-80 transition-opacity relative group cursor-pointer bg-transparent border-none"
            >
              {{ item.label }}
              <span
                class="absolute bottom-0 left-0 h-0.5 bg-on-primary transition-all duration-200"
                [class.w-full]="activeSection() === item.fragment"
                [class.w-0]="activeSection() !== item.fragment"
                [class.group-hover:w-full]="activeSection() !== item.fragment"
              ></span>
            </button>
          }
        </nav>

        <!-- Desktop Contact Buttons -->
        <div class="hidden md:flex items-center space-x-3 lg:space-x-4 flex-shrink-0">

          <!-- Language Switcher -->
          <button
            (click)="langService.toggleLanguage()"
            class="cursor-pointer flex items-center space-x-1.5 text-on-primary border border-on-primary/40 px-3 py-1.5 rounded-full text-sm hover:bg-on-primary/10 transition-all duration-200"
            [attr.aria-label]="'Switch language'"
          >
            <lucide-icon name="globe" [size]="14"></lucide-icon>
            <span class="font-medium">{{ langService.currentLang() === 'fr' ? 'EN' : 'FR' }}</span>
          </button>

          <button
            (click)="navigateToDevis()"
            class="cursor-pointer border-2 border-on-primary text-on-primary px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base transition-all duration-200 flex items-center space-x-2 whitespace-nowrap"
          >
            <lucide-icon name="file-text" [size]="16" class="lg:w-5 lg:h-5"></lucide-icon>
            <span>{{ 'header.devis' | translate }}</span>
          </button>

          @if (isAuthenticated()) {
            <div class="flex items-center space-x-4">
              <span class="text-on-primary font-medium">{{ fullName() }}</span>
              <button
                (click)="logout()"
                class=" cursor-pointer border-2 border-on-primary text-on-primary px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base hover:bg-on-primary hover:text-primary transition-all duration-200"
              >
                {{ 'header.deconnexion' | translate }}
              </button>
            </div>
          } @else {
            <button
              (click)="openAuthDialog()"
              class="cursor-pointer border-2 border-on-primary text-on-primary px-4 lg:px-6 py-2 rounded-full font-medium text-sm lg:text-base transition-all duration-200 flex items-center space-x-2 whitespace-nowrap"
            >
              <img src="/icons/tel-icon.png" alt="Contact" class="w-4 h-4 lg:w-5 lg:h-5" />
              <span>{{ 'header.connexion' | translate }}</span>
            </button>
          }
        </div>
      }

      <!-- Mobile Menu Button -->
      @if (isLessThanMD$ | async) {
        <div class="flex items-center space-x-2">
          <!-- Language Switcher (Mobile) -->
          <button
            (click)="langService.toggleLanguage()"
            class="cursor-pointer flex items-center space-x-1 text-on-primary border border-on-primary/40 px-2.5 py-1 rounded-full text-xs hover:bg-on-primary/10 transition-all duration-200"
          >
            <lucide-icon name="globe" [size]="12"></lucide-icon>
            <span class="font-medium">{{ langService.currentLang() === 'fr' ? 'EN' : 'FR' }}</span>
          </button>

          <button
            (click)="toggleMobileMenu()"
            [attr.aria-expanded]="mobileMenuOpen()"
            aria-label="Toggle menu"
            class="md:hidden p-2 text-on-primary hover:opacity-80 transition-opacity"
          >
            @if (!mobileMenuOpen()) {
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            } @else {
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            }
          </button>
        </div>
      }
    </div>
  </div>

  <!-- Mobile Menu -->
  @if (isLessThanMD$ | async) {
    <div
      class="md:hidden overflow-hidden transition-all duration-300 ease-in-out border-t border-white/10"
      [class.max-h-0]="!mobileMenuOpen()"
      [class.max-h-screen]="mobileMenuOpen()"
    >
      <nav class="px-4 pt-2 pb-3 space-y-1">
        @for (item of navItems(); track item.label) {
          <button
            (click)="navigateToSection(item)"
            [class.font-bold]="activeSection() === item.fragment"
            [class.font-medium]="activeSection() !== item.fragment"
            class="w-full text-left block text-on-primary py-3 px-2 border-b border-white/10 hover:opacity-80 transition-opacity bg-transparent border-x-0 border-t-0"
          >
            {{ item.label }}
          </button>
        }
      </nav>

      <div class="px-4 py-4 space-y-3 border-t border-white/10">
        <button
          (click)="navigateToDevis()"
          class="w-full border-2 border-on-primary text-on-primary px-6 py-2.5 rounded-full font-medium hover:shadow-lg transition-all duration-200 flex items-center justify-center space-x-2"
        >
          <lucide-icon name="file-text" [size]="20"></lucide-icon>
          <span>{{ 'header.devis' | translate }}</span>
        </button>

        @if (isAuthenticated()) {
          <div class="flex flex-col space-y-2">
            <span class="text-on-primary text-center font-medium">{{ fullName() }}</span>
            <button
              (click)="logout()"
              class="w-full border-2 border-on-primary text-on-primary px-6 py-2.5 rounded-full font-medium hover:bg-on-primary hover:text-primary transition-all duration-200"
            >
              {{ 'header.deconnexion' | translate }}
            </button>
          </div>
        } @else {
          <button
            (click)="openAuthDialog()"
            class="w-full border-2 border-on-primary text-on-primary px-6 py-2.5 rounded-full font-medium hover:shadow-lg transition-all duration-200 flex items-center justify-center space-x-2"
          >
            <span>{{ 'header.connexion' | translate }}</span>
          </button>
        }
      </div>
    </div>
  }
</header>
